import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { showSnackbar } from '@/stores/snackbarStore';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const MAX_CAPTURE_WIDTH = 1280;
const MAX_CAPTURE_HEIGHT = 900;

function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

async function captureScreenFrame(): Promise<string> {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error('Screenshot capture is not available in this browser.');
  }

  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        frameRate: 1,
      },
      audio: false,
    });

    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();

    await new Promise<void>((resolve) => {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        resolve();
        return;
      }
      video.onloadeddata = () => resolve();
    });

    const sourceWidth = video.videoWidth || window.innerWidth;
    const sourceHeight = video.videoHeight || window.innerHeight;
    const scale = Math.min(1, MAX_CAPTURE_WIDTH / sourceWidth, MAX_CAPTURE_HEIGHT / sourceHeight);
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare screenshot.');
    ctx.drawImage(video, 0, 0, width, height);

    return canvas.toDataURL('image/jpeg', 0.82);
  } finally {
    stopStream(stream);
  }
}

export function BugReportModal({ isOpen, onClose }: BugReportModalProps) {
  const token = useAuthStore((state) => state.token);
  const [message, setMessage] = useState('');
  const [screenshotDataUrl, setScreenshotDataUrl] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hideForCapture, setHideForCapture] = useState(false);

  const reset = useCallback(() => {
    setMessage('');
    setScreenshotDataUrl(null);
    setIsCapturing(false);
    setIsSubmitting(false);
    setHideForCapture(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    reset();
    onClose();
  }, [isSubmitting, onClose, reset]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleClose, isOpen]);

  const handleCapture = async () => {
    setIsCapturing(true);
    setHideForCapture(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const dataUrl = await captureScreenFrame();
      setScreenshotDataUrl(dataUrl);
      showSnackbar('Screenshot captured', 'success');
    } catch (error) {
      showSnackbar(
        error instanceof Error ? error.message : 'Could not capture screenshot',
        'error'
      );
    } finally {
      setHideForCapture(false);
      setIsCapturing(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) {
      showSnackbar('Please log in to send a report', 'warning');
      return;
    }
    if (!screenshotDataUrl) {
      showSnackbar('Capture a screenshot before sending', 'warning');
      return;
    }
    const trimmedMessage = message.trim();
    if (trimmedMessage.length < 5) {
      showSnackbar('Add a short note before sending', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.createBugReport(token, {
        message: trimmedMessage,
        screenshotDataUrl,
        pageUrl: window.location.href,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
      showSnackbar('Bug report sent', 'success');
      reset();
      onClose();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Could not send report', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity ${
        hideForCapture ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={handleClose} />

      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-lg bg-wall-surface border border-wall-border rounded-xl shadow-2xl animate-scale-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bug-report-title"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-wall-border/50">
          <h2 id="bug-report-title" className="text-lg font-semibold text-wall-text">
            Report a bug
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-lg text-wall-muted hover:text-wall-text hover:bg-wall-border/30 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-wall-muted uppercase tracking-wide mb-2">
              Screenshot
            </label>
            {screenshotDataUrl ? (
              <div className="overflow-hidden rounded-lg border border-wall-border bg-wall-bg">
                <img
                  src={screenshotDataUrl}
                  alt="Bug report screenshot preview"
                  className="w-full max-h-56 object-contain"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={handleCapture}
                disabled={isCapturing || isSubmitting}
                className="w-full min-h-32 rounded-lg border border-dashed border-wall-border bg-wall-bg/70 text-wall-muted hover:text-wall-text hover:border-wall-accent/40 transition-colors disabled:opacity-50"
              >
                {isCapturing ? 'Capturing...' : 'Capture screenshot'}
              </button>
            )}
            {screenshotDataUrl && (
              <button
                type="button"
                onClick={handleCapture}
                disabled={isCapturing || isSubmitting}
                className="mt-2 text-sm text-wall-accent hover:underline disabled:opacity-50"
              >
                Retake screenshot
              </button>
            )}
          </div>

          <div>
            <label
              htmlFor="bug-report-message"
              className="block text-xs font-medium text-wall-muted uppercase tracking-wide mb-2"
            >
              What happened?
            </label>
            <textarea
              id="bug-report-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              maxLength={2000}
              rows={5}
              className="w-full resize-none px-3 py-2 rounded-lg bg-wall-bg border border-wall-border text-wall-text text-sm placeholder:text-wall-muted focus:outline-hidden focus:ring-2 focus:ring-wall-accent/50"
              placeholder="What were you trying to do?"
              disabled={isSubmitting}
            />
            <div className="mt-1 text-right text-[11px] text-wall-muted">
              {message.length.toLocaleString()}/2,000
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-wall-border/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 bg-wall-bg border border-wall-border rounded-lg text-sm font-medium hover:bg-wall-border/50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isCapturing}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-linear-to-r from-wall-accent to-wall-accent-secondary text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            Send report
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
